import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  useAddPremium,
  useAddValueSnapshot,
  useDeletePremium,
  useDeleteValueSnapshot,
  useHardDeleteInsurance,
  useInsurance,
  useSoftDeleteInsurance,
  useUpdateInsurance,
} from "../hooks/insurance";
import type {
  AssetClass,
  InsuranceKind,
  InsuranceRhythm,
  InsuranceValueSnapshot,
  UpdateInsuranceInput,
} from "../lib/insurance";

const KINDS: InsuranceKind[] = [
  "KV",
  "HAFTPFLICHT",
  "HAUSRAT",
  "BU",
  "LV",
  "AUTO",
  "RECHTSSCHUTZ",
  "REISE",
  "SONSTIGE",
];

const RHYTHMS: InsuranceRhythm[] = ["MONATLICH", "QUARTALSWEISE", "HALBJAEHRLICH", "JAEHRLICH"];

function formatEur(cents: number | null): string {
  if (cents === null) return "—";
  return (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString("de-DE", { timeZone: "UTC" });
}

function currentYearMonth(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

export function InsuranceDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const detail = useInsurance(id ?? "");
  const softDelete = useSoftDeleteInsurance();
  const hardDelete = useHardDeleteInsurance();
  const update = useUpdateInsurance();
  const addPremium = useAddPremium();
  const deletePremium = useDeletePremium();
  const [editOpen, setEditOpen] = useState(false);

  const [premiumValidFromMonth, setPremiumValidFromMonth] = useState(currentYearMonth());
  const [premiumAmountEur, setPremiumAmountEur] = useState("");
  const [premiumRhythm, setPremiumRhythm] = useState<InsuranceRhythm>("JAEHRLICH");

  if (detail.isLoading || !detail.data) {
    return (
      <div className="fv-card p-10 text-center" style={{ color: "var(--muted)" }}>
        …
      </div>
    );
  }
  const ins = detail.data;

  async function handleAddPremium(e: React.FormEvent) {
    e.preventDefault();
    if (!id) return;
    await addPremium.mutateAsync({
      id,
      input: {
        validFrom: `${premiumValidFromMonth}-01`,
        amountCents: Math.round(Number(premiumAmountEur) * 100),
        rhythm: premiumRhythm,
      },
    });
    setPremiumAmountEur("");
  }

  return (
    <section className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs mb-2" style={{ color: "var(--muted)" }}>
            <Link to="/insurance" className="hover:underline">
              {t("nav.insurance")}
            </Link>
            <span>/</span>
            <span style={{ color: "var(--text)" }}>{ins.name}</span>
          </div>
          <h1 className="text-2xl font-bold tracking-tight">{ins.name}</h1>
          <p className="text-sm mt-1" style={{ color: "var(--muted)" }}>
            {t(`insurance.kind.${ins.kind}`)} · {ins.insurer}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            className="px-3 py-2 rounded-lg border text-sm font-medium"
            style={{ borderColor: "var(--border)", background: "var(--card)" }}
            onClick={() => setEditOpen(true)}
          >
            {t("insurance.detail.edit", { defaultValue: "Bearbeiten" })}
          </button>
          {ins.isActive ? (
            <button
              type="button"
              className="px-3 py-2 rounded-lg border text-sm font-medium"
              style={{ borderColor: "var(--border)", background: "var(--card)" }}
              onClick={() => softDelete.mutate(ins.id)}
            >
              {t("insurance.detail.deactivate")}
            </button>
          ) : (
            <button
              type="button"
              className="px-3 py-2 rounded-lg border text-sm font-medium"
              style={{ borderColor: "var(--border)", background: "var(--card)" }}
              onClick={() => update.mutate({ id: ins.id, input: { isActive: true } })}
            >
              {t("insurance.detail.activate", { defaultValue: "Reaktivieren" })}
            </button>
          )}
          <button
            type="button"
            className="px-3 py-2 rounded-lg border text-sm font-medium"
            style={{ borderColor: "var(--border)", background: "var(--card)", color: "var(--neg)" }}
            onClick={async () => {
              if (window.confirm(t("insurance.detail.confirm_delete"))) {
                await hardDelete.mutateAsync(ins.id);
                nav("/insurance");
              }
            }}
          >
            {t("insurance.detail.delete")}
          </button>
        </div>
      </div>

      <section className="fv-card p-5 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <Field label={t("insurance.detail.current_premium")}>
          {ins.currentPremium
            ? `${formatEur(ins.currentPremium.amountCents)} / ${t(
                `insurance.rhythm.${ins.currentPremium.rhythm}`,
              )}`
            : "—"}
        </Field>
        <Field label={t("insurance.detail.monthly_equiv")}>
          {formatEur(ins.monthlyEquivalentCents)}
        </Field>
        <Field label={t("insurance.detail.contract_number")}>{ins.contractNumber ?? "—"}</Field>
        <Field label={t("insurance.detail.start_date")}>{formatDate(ins.startDate)}</Field>
        <Field label={t("insurance.detail.end_date")}>{formatDate(ins.endDate)}</Field>
        <Field label={t("insurance.detail.notice_period_days")}>
          {ins.noticePeriodDays ?? "—"}
        </Field>
        <Field label={t("insurance.detail.next_renewal")}>{formatDate(ins.nextRenewalDate)}</Field>
        <Field label={t("insurance.detail.notice_deadline")}>
          {formatDate(ins.noticeDeadlineDate)}
        </Field>
        <Field label={t("insurance.detail.coverage_sum")}>{formatEur(ins.coverageSumCents)}</Field>
        <Field label={t("insurance.detail.deductible")}>{formatEur(ins.deductibleCents)}</Field>
        {ins.valueAssetClass && (
          <Field
            label={t("insurance.detail.current_value", {
              defaultValue: "Aktueller Wert",
            })}
          >
            {ins.currentValueCents !== null ? formatEur(ins.currentValueCents) : "—"}
          </Field>
        )}
        {ins.tags.length > 0 && (
          <div className="col-span-2 md:col-span-4">
            <p className="text-xs text-slate-500">{t("insurance.detail.tags")}</p>
            <p className="mt-0.5">
              {ins.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-block bg-slate-100 text-slate-700 text-xs px-2 py-0.5 rounded mr-1"
                >
                  {tag}
                </span>
              ))}
            </p>
          </div>
        )}
        {ins.notes && (
          <div className="col-span-2 md:col-span-4">
            <p className="text-xs text-slate-500">{t("insurance.detail.notes")}</p>
            <p className="mt-0.5 whitespace-pre-wrap">{ins.notes}</p>
          </div>
        )}
      </section>

      {ins.valueAssetClass && (
        <InsuranceValueHistorySection
          insuranceId={ins.id}
          valueAssetClass={ins.valueAssetClass}
          currentValueCents={ins.currentValueCents}
          snapshots={ins.valueSnapshots}
        />
      )}

      <section className="bg-white border rounded p-4">
        <h2 className="font-semibold mb-1">{t("insurance.detail.premiums_title")}</h2>
        <p className="text-xs text-slate-500 mb-3">
          {t("insurance.detail.premium_history_hint")}
        </p>
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-left border-b">
              <th className="p-2">{t("insurance.detail.column.valid_from")}</th>
              <th className="p-2 text-right">{t("insurance.detail.column.amount")}</th>
              <th className="p-2">{t("insurance.detail.column.rhythm")}</th>
              <th className="p-2 text-right">{t("insurance.detail.column.monthly_equiv")}</th>
              <th className="p-2">{t("insurance.detail.column.cashflow")}</th>
              <th className="p-2" />
            </tr>
          </thead>
          <tbody>
            {ins.premiums.map((p) => (
              <tr key={p.id} className="border-b">
                <td className="p-2">{formatDate(p.validFrom)}</td>
                <td className="p-2 text-right font-mono">{formatEur(p.amountCents)}</td>
                <td className="p-2">{t(`insurance.rhythm.${p.rhythm}`)}</td>
                <td className="p-2 text-right font-mono">
                  {formatEur(
                    Math.round(
                      p.amountCents /
                        ({ MONATLICH: 1, QUARTALSWEISE: 3, HALBJAEHRLICH: 6, JAEHRLICH: 12 }[
                          p.rhythm
                        ] ?? 1),
                    ),
                  )}
                </td>
                <td className="p-2">
                  {p.cashflowPositionId && (
                    <Link
                      to={`/cashflow/${p.cashflowPositionId}`}
                      className="text-blue-600 text-xs underline"
                      title={t("insurance.detail.cashflow_link_tooltip") ?? undefined}
                    >
                      {t("insurance.detail.cashflow_link")}
                    </Link>
                  )}
                </td>
                <td className="p-2">
                  {ins.premiums.length > 1 && (
                    <button
                      type="button"
                      className="text-red-700 text-sm"
                      onClick={() => deletePremium.mutate({ id: ins.id, premiumId: p.id })}
                    >
                      {t("insurance.detail.column.delete")}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <form
          onSubmit={handleAddPremium}
          className="mt-4 border-t pt-3 flex flex-wrap gap-2 items-end"
        >
          <label className="text-sm">
            <span className="text-slate-600">{t("insurance.detail.add_valid_from")}</span>
            <input
              type="month"
              required
              className="border rounded px-2 py-1 block"
              value={premiumValidFromMonth}
              onChange={(e) => setPremiumValidFromMonth(e.target.value)}
            />
          </label>
          <label className="text-sm">
            <span className="text-slate-600">
              {t("insurance.detail.add_amount")}{" "}
              <span className="text-xs" style={{ color: "var(--muted)" }}>
                {t("insurance.detail.zero_hint", {
                  defaultValue: "(0 = beitragsfrei)",
                })}
              </span>
            </span>
            <input
              type="number"
              step="0.01"
              min={0}
              required
              className="border rounded px-2 py-1 block w-32"
              value={premiumAmountEur}
              onChange={(e) => setPremiumAmountEur(e.target.value)}
              placeholder="0"
            />
          </label>
          <label className="text-sm">
            <span className="text-slate-600">{t("insurance.detail.add_rhythm")}</span>
            <select
              className="border rounded px-2 py-1 block"
              value={premiumRhythm}
              onChange={(e) => setPremiumRhythm(e.target.value as InsuranceRhythm)}
            >
              {RHYTHMS.map((r) => (
                <option key={r} value={r}>
                  {t(`insurance.rhythm.${r}`)}
                </option>
              ))}
            </select>
          </label>
          <button
            type="submit"
            disabled={addPremium.isPending}
            className="px-3 py-1 bg-blue-600 text-white rounded text-sm disabled:opacity-50"
          >
            {t("insurance.detail.add_premium")}
          </button>
        </form>
      </section>

      {editOpen && (
        <EditInsuranceModal
          initial={{
            name: ins.name,
            kind: ins.kind,
            insurer: ins.insurer,
            contractNumber: ins.contractNumber,
            startDate: ins.startDate,
            endDate: ins.endDate,
            noticePeriodDays: ins.noticePeriodDays,
            coverageSumCents: ins.coverageSumCents,
            deductibleCents: ins.deductibleCents,
            notes: ins.notes,
            tags: ins.tags,
            valueAssetClass: ins.valueAssetClass,
          }}
          onClose={() => setEditOpen(false)}
          onSubmit={async (input) => {
            await update.mutateAsync({ id: ins.id, input });
            setEditOpen(false);
          }}
          submitting={update.isPending}
        />
      )}
    </section>
  );
}

interface EditInsuranceModalProps {
  initial: {
    name: string;
    kind: InsuranceKind;
    insurer: string;
    contractNumber: string | null;
    startDate: string;
    endDate: string | null;
    noticePeriodDays: number | null;
    coverageSumCents: number | null;
    deductibleCents: number | null;
    notes: string | null;
    tags: string[];
    valueAssetClass: AssetClass | null;
  };
  onClose: () => void;
  onSubmit: (input: UpdateInsuranceInput) => Promise<void>;
  submitting: boolean;
}

const VALUE_ASSET_CLASSES: { value: AssetClass; labelKey: string; defaultLabel: string }[] = [
  { value: "ANLAGEN", labelKey: "asset_class.ANLAGEN", defaultLabel: "Anlagen" },
  { value: "SONSTIGE", labelKey: "asset_class.SONSTIGE", defaultLabel: "Sonstiges" },
  { value: "LIQUIDE", labelKey: "asset_class.LIQUIDE", defaultLabel: "Liquide" },
  { value: "IMMOBILIE", labelKey: "asset_class.IMMOBILIE", defaultLabel: "Immobilie" },
];

function EditInsuranceModal({ initial, onClose, onSubmit, submitting }: EditInsuranceModalProps) {
  const { t } = useTranslation();
  const [name, setName] = useState(initial.name);
  const [kind, setKind] = useState<InsuranceKind>(initial.kind);
  const [insurer, setInsurer] = useState(initial.insurer);
  const [contractNumber, setContractNumber] = useState(initial.contractNumber ?? "");
  const [startDate, setStartDate] = useState(initial.startDate);
  const [endDate, setEndDate] = useState(initial.endDate ?? "");
  const [noticePeriodDays, setNoticePeriodDays] = useState(
    initial.noticePeriodDays !== null ? String(initial.noticePeriodDays) : "",
  );
  const [coverageSumEur, setCoverageSumEur] = useState(
    initial.coverageSumCents !== null ? String(initial.coverageSumCents / 100) : "",
  );
  const [deductibleEur, setDeductibleEur] = useState(
    initial.deductibleCents !== null ? String(initial.deductibleCents / 100) : "",
  );
  const [notes, setNotes] = useState(initial.notes ?? "");
  const [tagsRaw, setTagsRaw] = useState(initial.tags.join(", "));
  const [valueAssetClass, setValueAssetClass] = useState<AssetClass | "">(
    initial.valueAssetClass ?? "",
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await onSubmit({
        name: name.trim(),
        kind,
        insurer: insurer.trim(),
        contractNumber: contractNumber.trim() ? contractNumber.trim() : null,
        startDate,
        endDate: endDate ? endDate : null,
        noticePeriodDays: noticePeriodDays ? Number(noticePeriodDays) : null,
        coverageSumCents: coverageSumEur ? Math.round(Number(coverageSumEur) * 100) : null,
        deductibleCents: deductibleEur ? Math.round(Number(deductibleEur) * 100) : null,
        notes: notes.trim() ? notes.trim() : null,
        tags: tagsRaw.split(",").map((s) => s.trim()).filter(Boolean),
        valueAssetClass: valueAssetClass === "" ? null : valueAssetClass,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div
        className="rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        style={{ background: "var(--card)", border: "1px solid var(--border)" }}
      >
        <div
          className="px-5 py-4 border-b sticky top-0"
          style={{ borderColor: "var(--border)", background: "var(--card)" }}
        >
          <h2 className="text-base font-semibold">
            {t("insurance.detail.edit_title", { defaultValue: "Versicherung bearbeiten" })}
          </h2>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-3">
          <ModalField label={t("insurance.form.name", { defaultValue: "Name" })}>
            <ModalInput value={name} onChange={(v) => setName(v)} required />
          </ModalField>
          <div className="grid grid-cols-2 gap-3">
            <ModalField label={t("insurance.form.kind", { defaultValue: "Art" })}>
              <ModalSelect
                value={kind}
                onChange={(v) => setKind(v as InsuranceKind)}
              >
                {KINDS.map((k) => (
                  <option key={k} value={k}>
                    {t(`insurance.kind.${k}`)}
                  </option>
                ))}
              </ModalSelect>
            </ModalField>
            <ModalField label={t("insurance.form.insurer", { defaultValue: "Versicherer" })}>
              <ModalInput value={insurer} onChange={(v) => setInsurer(v)} required />
            </ModalField>
          </div>
          <ModalField
            label={t("insurance.form.contract_number", { defaultValue: "Vertragsnummer" })}
          >
            <ModalInput value={contractNumber} onChange={(v) => setContractNumber(v)} />
          </ModalField>
          <div className="grid grid-cols-2 gap-3">
            <ModalField label={t("insurance.form.start_date", { defaultValue: "Vertragsbeginn" })}>
              <ModalInput
                type="date"
                value={startDate}
                onChange={(v) => setStartDate(v)}
                required
              />
            </ModalField>
            <ModalField
              label={t("insurance.form.end_date", { defaultValue: "Vertragsende" })}
              hint={t("insurance.form.end_date_hint", {
                defaultValue: "Leer = unbefristet",
              })}
            >
              <ModalInput type="date" value={endDate} onChange={(v) => setEndDate(v)} />
            </ModalField>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <ModalField
              label={t("insurance.form.notice_period_days", {
                defaultValue: "Kündigungsfrist (Tage)",
              })}
            >
              <ModalInput
                type="number"
                value={noticePeriodDays}
                onChange={(v) => setNoticePeriodDays(v)}
              />
            </ModalField>
            <ModalField label={t("insurance.form.coverage_sum", { defaultValue: "VS (EUR)" })}>
              <ModalInput
                type="number"
                step="100"
                value={coverageSumEur}
                onChange={(v) => setCoverageSumEur(v)}
              />
            </ModalField>
            <ModalField
              label={t("insurance.form.deductible", { defaultValue: "Selbstbeteiligung (EUR)" })}
            >
              <ModalInput
                type="number"
                step="10"
                value={deductibleEur}
                onChange={(v) => setDeductibleEur(v)}
              />
            </ModalField>
          </div>
          <ModalField
            label={t("insurance.form.tags", { defaultValue: "Tags (komma-getrennt)" })}
          >
            <ModalInput value={tagsRaw} onChange={(v) => setTagsRaw(v)} />
          </ModalField>

          <div
            className="rounded-lg p-3 border"
            style={{
              borderColor: "var(--border)",
              background: "var(--card-elev)",
            }}
          >
            <ModalField
              label={t("insurance.form.value_asset_class", {
                defaultValue: "Werthaltend (z.B. fondsgebundene LV)",
              })}
              hint={t("insurance.form.value_asset_class_hint", {
                defaultValue:
                  "Wenn gesetzt: Wert fließt in die gewählte Klasse bei Vermögen + Allokation. Wertverlauf wird im Detail manuell gepflegt.",
              })}
            >
              <ModalSelect
                value={valueAssetClass}
                onChange={(v) => setValueAssetClass(v as AssetClass | "")}
              >
                <option value="">
                  {t("insurance.form.value_asset_class.none", {
                    defaultValue: "Reine Risiko-/Beitrags-Versicherung",
                  })}
                </option>
                {VALUE_ASSET_CLASSES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {t(c.labelKey, { defaultValue: c.defaultLabel })}
                  </option>
                ))}
              </ModalSelect>
            </ModalField>
          </div>
          <ModalField label={t("insurance.form.notes", { defaultValue: "Notizen" })}>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-2 py-1.5 rounded-lg text-sm"
              style={{
                background: "var(--card)",
                border: "1px solid var(--border)",
                color: "var(--text)",
              }}
            />
          </ModalField>

          {error && (
            <div
              className="text-sm px-3 py-2 rounded-lg"
              style={{ background: "var(--neg-soft)", color: "var(--neg)" }}
            >
              {error}
            </div>
          )}

          <div
            className="flex gap-2 justify-end pt-3 border-t"
            style={{ borderColor: "var(--border)" }}
          >
            <button
              type="button"
              className="px-3 py-2 rounded-lg border text-sm font-medium"
              style={{ borderColor: "var(--border)", background: "var(--card)" }}
              onClick={onClose}
            >
              {t("common.cancel", { defaultValue: "Abbrechen" })}
            </button>
            <button
              type="submit"
              className="px-3 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-50"
              style={{ background: "var(--accent)" }}
              disabled={submitting}
            >
              {submitting ? "…" : t("common.save", { defaultValue: "Speichern" })}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ModalField({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <div
        className="text-xs font-semibold uppercase tracking-wider mb-1"
        style={{ color: "var(--muted)" }}
      >
        {label}
      </div>
      {children}
      {hint && (
        <div className="text-[11px] mt-1" style={{ color: "var(--muted)" }}>
          {hint}
        </div>
      )}
    </label>
  );
}

function ModalInput({
  value,
  onChange,
  type = "text",
  step,
  required,
}: {
  value: string;
  onChange: (v: string) => void;
  type?: string;
  step?: string;
  required?: boolean;
}) {
  return (
    <input
      type={type}
      step={step}
      required={required}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-2 py-1.5 rounded-lg text-sm"
      style={{
        background: "var(--card)",
        border: "1px solid var(--border)",
        color: "var(--text)",
      }}
    />
  );
}

function ModalSelect({
  value,
  onChange,
  children,
}: {
  value: string;
  onChange: (v: string) => void;
  children: React.ReactNode;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-2 py-1.5 rounded-lg text-sm"
      style={{
        background: "var(--card)",
        border: "1px solid var(--border)",
        color: "var(--text)",
      }}
    >
      {children}
    </select>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs text-slate-500">{label}</p>
      <p className="font-mono mt-0.5">{children}</p>
    </div>
  );
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function InsuranceValueHistorySection({
  insuranceId,
  valueAssetClass,
  currentValueCents,
  snapshots,
}: {
  insuranceId: string;
  valueAssetClass: AssetClass;
  currentValueCents: number | null;
  snapshots: InsuranceValueSnapshot[];
}) {
  const { t } = useTranslation();
  const addSnap = useAddValueSnapshot();
  const delSnap = useDeleteValueSnapshot();
  const [asOf, setAsOf] = useState(todayIso());
  const [valueEur, setValueEur] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await addSnap.mutateAsync({
        id: insuranceId,
        input: {
          asOf,
          valueCents: Math.round(Number(valueEur) * 100),
          notes: notes.trim() || undefined,
        },
      });
      setValueEur("");
      setNotes("");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <section className="fv-card p-5">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h2 className="font-semibold">
            {t("insurance.detail.value_history.title", { defaultValue: "Wertverlauf" })}
          </h2>
          <p className="text-xs mt-0.5" style={{ color: "var(--muted)" }}>
            {t("insurance.detail.value_history.hint", {
              defaultValue:
                "Fließt in {{cls}} bei Vermögen und Allokation. Manuell pflegen (z.B. nach Jahresmeldung).",
              cls: t(`asset_class.${valueAssetClass}`, { defaultValue: valueAssetClass }),
            })}
          </p>
        </div>
        <div className="text-right">
          <div className="text-xs" style={{ color: "var(--muted)" }}>
            {t("insurance.detail.value_history.current", { defaultValue: "Aktueller Wert" })}
          </div>
          <div className="text-xl font-bold num">
            {currentValueCents !== null
              ? (currentValueCents / 100).toLocaleString("de-DE", {
                  style: "currency",
                  currency: "EUR",
                })
              : "—"}
          </div>
        </div>
      </div>

      <form
        onSubmit={handleAdd}
        className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end mb-4"
      >
        <label className="block">
          <div className="text-xs mb-1" style={{ color: "var(--muted)" }}>
            {t("insurance.detail.value_history.as_of", { defaultValue: "Stichtag" })}
          </div>
          <input
            type="date"
            value={asOf}
            onChange={(e) => setAsOf(e.target.value)}
            required
            className="w-full px-2 py-1.5 rounded-lg text-sm"
            style={{
              background: "var(--card)",
              border: "1px solid var(--border)",
              color: "var(--text)",
            }}
          />
        </label>
        <label className="block">
          <div className="text-xs mb-1" style={{ color: "var(--muted)" }}>
            {t("insurance.detail.value_history.value_eur", {
              defaultValue: "Wert (EUR)",
            })}
          </div>
          <input
            type="number"
            step="0.01"
            value={valueEur}
            onChange={(e) => setValueEur(e.target.value)}
            required
            className="w-full px-2 py-1.5 rounded-lg text-sm num"
            style={{
              background: "var(--card)",
              border: "1px solid var(--border)",
              color: "var(--text)",
            }}
          />
        </label>
        <label className="block md:col-span-1">
          <div className="text-xs mb-1" style={{ color: "var(--muted)" }}>
            {t("insurance.detail.value_history.notes", { defaultValue: "Notiz (optional)" })}
          </div>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full px-2 py-1.5 rounded-lg text-sm"
            style={{
              background: "var(--card)",
              border: "1px solid var(--border)",
              color: "var(--text)",
            }}
          />
        </label>
        <button
          type="submit"
          disabled={addSnap.isPending}
          className="px-3 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-50"
          style={{ background: "var(--accent)" }}
        >
          {addSnap.isPending
            ? "…"
            : t("insurance.detail.value_history.add", { defaultValue: "Snapshot hinzufügen" })}
        </button>
      </form>

      {error && (
        <div
          className="mb-3 text-sm px-3 py-2 rounded-lg"
          style={{ background: "var(--neg-soft)", color: "var(--neg)" }}
        >
          {error}
        </div>
      )}

      {snapshots.length === 0 ? (
        <div
          className="text-sm py-6 text-center"
          style={{ color: "var(--muted)" }}
        >
          {t("insurance.detail.value_history.empty", {
            defaultValue: "Noch keine Wert-Snapshots erfasst.",
          })}
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr
              className="text-xs uppercase tracking-wider text-left"
              style={{ color: "var(--muted)" }}
            >
              <th
                className="py-1.5 border-b"
                style={{ borderColor: "var(--border)" }}
              >
                {t("insurance.detail.value_history.as_of", { defaultValue: "Stichtag" })}
              </th>
              <th
                className="py-1.5 text-right border-b"
                style={{ borderColor: "var(--border)" }}
              >
                {t("insurance.detail.value_history.value_eur", { defaultValue: "Wert (EUR)" })}
              </th>
              <th
                className="py-1.5 border-b"
                style={{ borderColor: "var(--border)" }}
              >
                {t("insurance.detail.value_history.source", { defaultValue: "Quelle" })}
              </th>
              <th
                className="py-1.5 border-b"
                style={{ borderColor: "var(--border)" }}
              >
                {t("insurance.detail.value_history.notes", { defaultValue: "Notiz" })}
              </th>
              <th
                className="py-1.5 border-b w-10"
                style={{ borderColor: "var(--border)" }}
              />
            </tr>
          </thead>
          <tbody>
            {snapshots.map((s) => (
              <tr
                key={s.id}
                className="border-b"
                style={{ borderColor: "var(--border-soft)" }}
              >
                <td className="py-2">{s.asOf}</td>
                <td className="py-2 text-right num font-semibold">
                  {(s.valueCents / 100).toLocaleString("de-DE", {
                    style: "currency",
                    currency: "EUR",
                  })}
                </td>
                <td className="py-2 text-xs" style={{ color: "var(--muted)" }}>
                  {s.source}
                </td>
                <td className="py-2 text-xs" style={{ color: "var(--muted)" }}>
                  {s.notes ?? "—"}
                </td>
                <td className="py-2 text-right">
                  <button
                    type="button"
                    onClick={() => {
                      if (
                        window.confirm(
                          t("insurance.detail.value_history.confirm_delete", {
                            defaultValue: "Snapshot wirklich löschen?",
                          }),
                        )
                      ) {
                        delSnap.mutate({ id: insuranceId, snapshotId: s.id });
                      }
                    }}
                    className="text-xs"
                    style={{ color: "var(--neg)" }}
                  >
                    ✕
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
